/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useMemo } from "react"

import { useDispatch } from "react-redux"

import type { TEmptyCallback } from "@/shared/types/functions"

import type { IAppDispatch } from "../store"

import { EAppAnimationName, appReducerActions } from "./app.slice"

type TAppDispatcher = {
    toggleBurgerMenu(condition?: boolean): void
    toggleSidebar: TEmptyCallback
    openSidebar: TEmptyCallback
    closeSidebar: TEmptyCallback
    initApp: TEmptyCallback
    setVersionMismatch(condition: boolean): void
    completeAnimation(name: EAppAnimationName): void
    resetAnimation(name: EAppAnimationName): void
    eraseAnimations: TEmptyCallback
    resetState: TEmptyCallback
}

function useAppDispatcher(): ReturnType<() => TAppDispatcher> {
    const dispatch: IAppDispatch = useDispatch()

    const toggleBurgerMenu: (condition?: boolean) => void = useCallback(
        (condition?: boolean): void => void dispatch(appReducerActions.toggleBurgerMenu(condition)),
        [dispatch]
    )

    const toggleSidebar: TEmptyCallback = useCallback(
        (): void => void dispatch(appReducerActions.toggleSidebar(void 0)),
        [dispatch]
    )

    const openSidebar: TEmptyCallback = useCallback(
        (): void => void dispatch(appReducerActions.openSidebar(void 0)),
        [dispatch]
    )

    const closeSidebar: TEmptyCallback = useCallback(
        (): void => void dispatch(appReducerActions.closeSidebar(void 0)),
        [dispatch]
    )

    const initApp: TEmptyCallback = useCallback(
        (): void => void dispatch(appReducerActions.setAppInit(true)),
        [dispatch]
    )

    const setVersionMismatch: (condition: boolean) => void = useCallback(
        (condition: boolean): void => void dispatch(appReducerActions.setVersionMismatch(condition)),
        [dispatch]
    )

    const completeAnimation: (name: EAppAnimationName) => void = useCallback(
        (name: EAppAnimationName): void => void dispatch(appReducerActions.completeAnimation(name)),
        [dispatch]
    )

    const resetAnimation: (name: EAppAnimationName) => void = useCallback(
        (name: EAppAnimationName): void => void dispatch(appReducerActions.resetAnimation(name)),
        [dispatch]
    )

    const eraseAnimations: TEmptyCallback = useCallback(
        (): void => void dispatch(appReducerActions.clearAnimations(void 0)),
        [dispatch]
    )

    const resetState: TEmptyCallback = useCallback(
        (): void => void dispatch(appReducerActions.reset(void 0)),
        [dispatch]
    )

    return useMemo(
        (): TAppDispatcher => ({
            toggleBurgerMenu,
            toggleSidebar,
            openSidebar,
            closeSidebar,
            initApp,
            setVersionMismatch,
            completeAnimation,
            resetAnimation,
            eraseAnimations,
            resetState
        }),
        [dispatch]
    )
}

export { useAppDispatcher, type TAppDispatcher }
